import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, CardImgOverlay, CardHeader, CardTitle, CardBody, Button } from 'reactstrap';
import FontAwesome from 'react-fontawesome';

import { getContentBlockItemsByName, getContentBlockItemByName, getContentBlockMixedItem } from '../../../CmsActions';

import Breadcrumbs from '../../../../App/components/view/Breadcrumbs';
import HtmlComponent from '../../../../../components/Text/HtmlComponent';
import FileLoader from '../../../../../components/Content/FileLoader';
import CmsBlockItem from '../../content/CmsBlockItem';
import PreFooter from '../../../../App/components/PreFooter';

const PageDefault = ({ page }) => {
    const image = getContentBlockItemByName(page.blocks, 'image');
    const title = getContentBlockItemByName(page.blocks, 'title');
    const contents = getContentBlockItemsByName(page.blocks, 'content');
    const promotion = getContentBlockItemByName(page.blocks, 'promotion');

    const renderContent = (content, isFullColumn = false) => {
        const text = getContentBlockMixedItem(content, 'text') && <HtmlComponent text={getContentBlockMixedItem(content, 'text').text} />;
        const image = getContentBlockMixedItem(content, 'image') && getContentBlockMixedItem(content, 'image').path && <CmsBlockItem item={getContentBlockMixedItem(content, 'image')} type="image" className="mt-3 mt-md-0" />;
        const cta = getContentBlockMixedItem(content, 'call-to-action') && <div className="text-center"><Button color="warning" size="lg" tag={Link} to={getContentBlockMixedItem(content, 'call-to-action').path} className="mt-3">{getContentBlockMixedItem(content, 'call-to-action').text}</Button></div>;
        return (
            <div className="mb-5">
                {
                    !isFullColumn
                    ? (
                        <Fragment>
                            {image}
                            {text}
                            {cta}
                        </Fragment>
                    ) : (
                        <Row>
                            <Col xs="12" md={image ? 8 : 12}>
                                {text}
                                {cta}
                            </Col>
                            {image && <Col>{image}</Col>}
                        </Row>
                    )
                }
            </div>
        );
    };

    return (
        <Fragment>
            <Container>
                <Card>
                    {image && <FileLoader filename={image.path} loadonmount className="card-img" style={{ minHeight: '200px' }} />}
                    <CardImgOverlay>
                        <Breadcrumbs crumb={page && page.title} />
                        {title && (
                            <Row>
                                <Col xs="12" md={promotion ? 8 : 12}>
                                    <h1 className="my-0 my-lg-5 py-2 py-lg-5 text-white font-weight-bold">{title.text}</h1>
                                </Col>
                            </Row>
                        )}
                    </CardImgOverlay>
                </Card>
                <div className={classnames(image && 'override-block-lg')}>
                    {contents && contents.length && contents.map((content, index) => {
                        if(index === 0 && promotion) {
                            return (
                                <Row key={index} className="mb-3">
                                    <Col xs={{ size: 12, order: 2 }} md={{ size: 7, order: 1 }}>
                                        {renderContent(content)}
                                    </Col>
                                    <Col xs={{ size: 12, order: 1 }} md={{ size: 5, order: 2 }} className="mb-3">
                                        <div className="d-none d-md-block bg-tertiary" style={{ paddingTop: '120px' }}>
                                            {getContentBlockMixedItem(promotion, 'image') && (
                                                <div className="d-none d-md-block position-absolute w-100 text-center" style={{ top: '-300px', left: 0 }}>
                                                    <CmsBlockItem item={getContentBlockMixedItem(promotion, 'image')} type="image" style={{ maxHeight: '450px' }} />
                                                </div>
                                            )}
                                            <div className="p-3">
                                                {getContentBlockMixedItem(promotion, 'title') && <span className="h6 font-weight-bold">{getContentBlockMixedItem(promotion, 'title').text}</span>}
                                                {getContentBlockMixedItem(promotion, 'call-to-action') && getContentBlockMixedItem(promotion, 'call-to-action').path && (
                                                    <Button color="secondary" block tag={Link} to={getContentBlockMixedItem(promotion, 'call-to-action').path} className="mt-3">{getContentBlockMixedItem(promotion, 'call-to-action').text}</Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-block d-md-none p-3 bg-tertiary">
                                            <div className="text-center"><CmsBlockItem item={getContentBlockMixedItem(promotion, 'image')} type="image" className="mb-3 w-75" /></div>
                                            {getContentBlockMixedItem(promotion, 'title') && <span className="h6 font-weight-bold">{getContentBlockMixedItem(promotion, 'title').text}</span>}
                                            {getContentBlockMixedItem(promotion, 'call-to-action') && getContentBlockMixedItem(promotion, 'call-to-action').path && (
                                                <Button color="secondary" block tag={Link} to={getContentBlockMixedItem(promotion, 'call-to-action').path} className="mt-3">{getContentBlockMixedItem(promotion, 'call-to-action').text}</Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            );
                        }
                        return renderContent(content, true);
                    })}
                </div>
            </Container>
            <PreFooter />
        </Fragment>
    );
};

PageDefault.propTypes = {
    page: PropTypes.object.isRequired,
};

export default PageDefault;

export const PageDefaultModel = {
    type: 'page',
    name: 'default',
    blocks: [
        {
            name: 'image', // name or id; MUST be unique
            max: 1, // max same items allowed
            type: 'image', // || text || link || image || mixed (with children)
        },
        {
            name: 'title', // name or id; MUST be unique
            max: 1, // max same items allowed
            type: 'text', // || text || link || image || mixed (with children)
        },
        {
            name: 'promotion',
            type: 'mixed',
            blocks: [
                {
                    name: 'title',
                    type: 'text',
                    max: 1,
                },
                {
                    name: 'image',
                    type: 'image',
                    max: 1,
                },
                {
                    name: 'call-to-action',
                    type: 'link',
                    max: 1,
                },
            ],
        },
        {
            name: 'content',
            type: 'mixed',
            blocks: [
                {
                    name: 'text', // name or id; MUST be unique
                    max: 1, // max same items allowed
                    type: 'html', // || text || link || image || mixed (with children)
                },
                {
                    name: 'image',
                    type: 'image',
                    max: 1,
                },
                {
                    name: 'call-to-action',
                    type: 'link',
                    max: 1,
                },
            ],
        },
    ],
};
